import moment from 'moment'
import { format, formatRelative } from '@fs/zion-locale/date-fns'
import { isNullOrUndefinedOrEmpty } from './Utils'

// Convert dates

export const TIME_FORMAT = ' HH:mm:ss'
export const MILLISECONDS_FORMAT = '.SSS'
export const ORIGINAL_DATE_FORMAT = 'DD MMM YYYY'
export const FORMAL_DATE_FORMAT = '+YYYY-MM-DD'
export const GENEALOGICAL_DATE_FORMAT = 'DD MMMM YYYY'
export const NUMERIC_DATE_FORMAT = 'YYYY-MM-DD'
export const YEAR_MONTH_DATE_TIME_FORMAT = 'YYYY MMMM DD, HH:mm:ss.SSS'

export const STANDARDS_DAY_MONTH_YEAR_FORMAT = 'DD MMMM YYYY'
export const STANDARDS_MONTH_YEAR_FORMAT = 'MMMM YYYY'
export const STANDARDS_YEAR_FORMAT = 'YYYY'

export function toTimestamp(aMoment) {
  return aMoment.toDate().getTime()
}

export function getFormatString(baseString, withTime, withMilliseconds) {
  return `${baseString}${withTime ? TIME_FORMAT : ''}${withMilliseconds ? MILLISECONDS_FORMAT : ''}`
}

export function convertDateString(inputString, inputFormat, outputFormat) {
  if (isNullOrUndefinedOrEmpty(inputString)) {
    return ''
  }
  // Special cases that moment does not handle
  if (inputFormat === 'yyyyMMDD') {
    inputString = `${inputString.substring(0, 4)}-${inputString.substring(4, 6)}-${inputString.substring(6)}`
    inputFormat = 'yyyy-MM-DD'
  }

  const timestamp = toTimestamp(moment(inputString, inputFormat))
  const outputString = moment(timestamp).format(outputFormat)
  return outputString
}

export function dateStringToTimestamp(str, dateFormat) {
  return toTimestamp(moment(str, `${dateFormat}${TIME_FORMAT}${MILLISECONDS_FORMAT}`))
}

export function timestampToDateString({
  timestamp,
  withTime,
  withMilliseconds,
  dateFormat = GENEALOGICAL_DATE_FORMAT,
  timestampIsSeconds,
}) {
  // Handles unexpected differences in Jackson 3 deserialization
  if (typeof timestamp === 'string') {
    if (timestamp.includes('T') && timestamp.includes('Z')) {
      return convertUTCString(timestamp, true)
    }
    return timestamp
  }
  const formatString = getFormatString(dateFormat, withTime, withMilliseconds)
  return moment(timestampIsSeconds ? timestamp * 1000 : timestamp).format(formatString)
}

export function UTCDateStringToTimestamp(utcString) {
  // Takes 2020-12-18T22:05:38.189Z, Fri Feb 13 22:12:23 UTC 2026, or +2023-08-26 (GEDCOM-X date)
  let timestamp

  // Check if it's in Unix date format (e.g., "Fri Feb 13 22:12:23 UTC 2026")
  // Pattern: starts with day name, contains UTC but not T (ISO format has T)
  if (utcString && typeof utcString === 'string' && /^[A-Z][a-z]{2}\s/.test(utcString) && utcString.includes(' UTC ')) {
    // Parse Unix date format: ddd MMM DD HH:mm:ss [UTC] YYYY
    // The [UTC] makes it a literal string that won't be interpreted as a format token
    timestamp = toTimestamp(moment.utc(utcString, 'ddd MMM DD HH:mm:ss [UTC] YYYY'))
  } else if (utcString && typeof utcString === 'string' && utcString.startsWith('+')) {
    // Parse GEDCOM-X formal date format e.g. +2023-08-26
    timestamp = toTimestamp(moment.utc(utcString.slice(1), 'YYYY-MM-DD'))
  } else {
    // Parse ISO format or other standard formats
    timestamp = toTimestamp(moment.utc(utcString))
  }

  return timestamp
}

export function convertUTCString(utcString, withTime, withMilliseconds, dateFormat = GENEALOGICAL_DATE_FORMAT) {
  const timestamp = UTCDateStringToTimestamp(utcString)
  return timestampToDateString({ timestamp, withTime, withMilliseconds, dateFormat })
}

export function toLocaleString(date, includeTime = false) {
  let result = ''
  try {
    if (date) {
      if (includeTime) {
        result = format(date, 'd MMMM yyyy p')
      } else {
        result = format(date)
      }
    }
  } catch (e) {
    result = ''
  }
  return result
}

export function utcToLocaleString(UTCStr, includeTime = false) {
  let localeDate = ''
  try {
    if (UTCStr) {
      const date = moment.utc(UTCStr, ['DD MMMM YYYY HH:mm', moment.ISO_8601], true).toDate()
      localeDate = toLocaleString(date, includeTime)
    }
  } catch (e) {
    localeDate = UTCStr
  }
  return localeDate
}

export function toLocaleRelativeString(date, dateToCompare) {
  try {
    return formatRelative(date, dateToCompare)
  } catch {
    return ''
  }
}

export function arrayToLocalDateTimeString(dateArray) {
  try {
    const [year, month, day, hour, minute, second] = dateArray
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
    return toLocaleString(date, true)
  } catch (e) {
    return ''
  }
}

export function getLocalTimeZoneAbbreviation() {
  return new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName').value
}

function toCompareMoment(dateOrOffset, dateFormat) {
  if (dateOrOffset instanceof Date || typeof dateOrOffset === 'number' || moment.isMoment(dateOrOffset)) {
    return moment(dateOrOffset)
  }
  if (typeof dateOrOffset === 'string') {
    return moment(dateOrOffset, dateFormat)
  }
  // Plain object treated as a duration offset from now: { months: 13 }, { days: 1 }, etc.
  return moment().subtract(dateOrOffset)
}

/**
 * Returns true if `dateStr` represents a calendar day strictly before the comparison target.
 * Returns false if `dateStr` is null, undefined, or empty.
 * Comparison is day-granular — time of day is ignored.
 *
 * The second argument accepts several forms:
 *   - A date string in the same format as `dateFormat` (default YYYY-MM-DD)
 *       isDateBefore('2025-01-01', '2025-06-01')         // true
 *   - A Date object or numeric timestamp
 *       isDateBefore('2020-01-01', new Date())           // true
 *   - A moment duration object — interpreted as an offset subtracted from today
 *       isDateBefore(startDate, { months: 13 })          // true if startDate is more than 13 months ago
 *       isDateBefore(startDate, { years: 2, days: 15 })  // combined offsets are supported
 *
 * @param {string} dateStr - The date to test, formatted according to `dateFormat`
 * @param {string|Date|number|moment.Moment|Object} dateOrOffset - The date or duration offset to compare against
 * @param {string} [dateFormat=NUMERIC_DATE_FORMAT] - moment format string for parsing `dateStr` (and `dateOrOffset` when it is a string)
 * @returns {boolean}
 */
export function isDateBefore(dateStr, dateOrOffset, dateFormat = NUMERIC_DATE_FORMAT) {
  if (isNullOrUndefinedOrEmpty(dateStr)) return false
  return moment(dateStr, dateFormat).isBefore(toCompareMoment(dateOrOffset, dateFormat), 'day')
}

/**
 * Returns true if `dateStr` represents a calendar day strictly after the comparison target.
 * Returns false if `dateStr` is null, undefined, or empty.
 * Comparison is day-granular — time of day is ignored.
 *
 * The second argument accepts several forms:
 *   - A date string in the same format as `dateFormat` (default YYYY-MM-DD)
 *       isDateAfter('2025-06-01', '2025-01-01')          // true
 *   - A Date object or numeric timestamp
 *       isDateAfter('2099-01-01', new Date())            // true
 *   - A moment duration object — interpreted as an offset subtracted from today
 *       isDateAfter(endDate, { days: 1 })                // true if endDate is today or in the future
 *       isDateAfter(endDate, { years: 1, months: 6 })    // combined offsets are supported
 *
 * @param {string} dateStr - The date to test, formatted according to `dateFormat`
 * @param {string|Date|number|moment.Moment|Object} dateOrOffset - The date or duration offset to compare against
 * @param {string} [dateFormat=NUMERIC_DATE_FORMAT] - moment format string for parsing `dateStr` (and `dateOrOffset` when it is a string)
 * @returns {boolean}
 */
export function isDateAfter(dateStr, dateOrOffset, dateFormat = NUMERIC_DATE_FORMAT) {
  if (isNullOrUndefinedOrEmpty(dateStr)) return false
  return moment(dateStr, dateFormat).isAfter(toCompareMoment(dateOrOffset, dateFormat), 'day')
}
